/**
 * VisualGest (Olifel) — Web service layer.
 *
 * As rotas Olifel vivem em /api/v2/olifel/* (e não em /api/v1 como Moloni).
 * O `axiosInstance` partilhado tem baseURL = `/api/v1/`, por isso aqui
 * construímos um prefix v2 a partir da config e usamos como path absoluto
 * nas chamadas — assim partilhamos auth header / interceptors com o
 * resto da app sem ter de criar uma instance separada.
 *
 * Fases cobertas:
 *   O.2 — connect, disconnect, status, auth-test, regenerate-inbound-token
 *   O.3 — initial-sync (start/status/cancel), sync-products, warehouses,
 *         families, doctypes, refresh-warehouses, refresh-families
 *   O.6 — invoice create/pdf/send-email, invoices list
 *   O.8 — pending-actions list/resolve, operation-log
 */

import axiosInstance from "./AxiosInstance";
import configDb from "../config";

const V2 = (configDb.data.api_url || "").replace(/\/api\/v1\/?$/, "/api/v2");

// ── O.2: ligação ─────────────────────────────────────────────────

export function connectOlifel(payload) {
    // payload = { email, password, empresa?, warehouse?, location?,
    //             default_familia?, default_tax_code?, default_price_id?,
    //             movement_strategy? }
    return axiosInstance.post(`${V2}/olifel/connect`, payload);
}

export function disconnectOlifel({ purge = false } = {}) {
    return axiosInstance.post(`${V2}/olifel/disconnect`, { purge });
}

export function getOlifelStatus() {
    return axiosInstance.get(`${V2}/olifel/status`);
}

export function olifelAuthTest() {
    return axiosInstance.post(`${V2}/olifel/auth-test`);
}

export function regenerateOlifelInboundToken() {
    return axiosInstance.post(`${V2}/olifel/regenerate-inbound-token`);
}

// ── O.3: sync ────────────────────────────────────────────────────

export function startOlifelInitialSync() {
    return axiosInstance.post(`${V2}/olifel/initial-sync/start`);
}

export function getOlifelInitialSyncStatus() {
    return axiosInstance.get(`${V2}/olifel/initial-sync/status`);
}

export function cancelOlifelInitialSync() {
    return axiosInstance.post(`${V2}/olifel/initial-sync/cancel`);
}

export function syncOlifelProducts({ partIds = [], partsQtyIds = [] } = {}) {
    return axiosInstance.post(`${V2}/olifel/sync-products`, { partIds, partsQtyIds });
}

// ── O.3: cache / lookup ──────────────────────────────────────────

export function getOlifelWarehouses({ refresh = false } = {}) {
    const qs = refresh ? "?refresh=1" : "";
    return axiosInstance.get(`${V2}/olifel/warehouses${qs}`);
}

export function getOlifelFamilies({ refresh = false } = {}) {
    const qs = refresh ? "?refresh=1" : "";
    return axiosInstance.get(`${V2}/olifel/families${qs}`);
}

export function getOlifelDoctypes() {
    return axiosInstance.get(`${V2}/olifel/doctypes`);
}

export function refreshOlifelWarehouses() {
    return axiosInstance.post(`${V2}/olifel/refresh-warehouses`);
}

export function refreshOlifelFamilies() {
    return axiosInstance.post(`${V2}/olifel/refresh-families`);
}

// ── O.6: facturas ────────────────────────────────────────────────

export function createOlifelInvoice(payload) {
    // payload = { internal_sales_id | source+source_id, send_email?, doc_type? }
    return axiosInstance.post(`${V2}/olifel/invoice/create`, payload);
}

export function getOlifelInvoiceList(filters = {}) {
    // filters = { page?, limit?, status?, source?, since? }
    return axiosInstance.post(`${V2}/olifel/invoices/list`, filters);
}

export function getOlifelInvoicePdfUrl(fullId) {
    // full_id tem `/` interno → vai como query string.
    return `${V2}/olifel/invoice/pdf?full_id=${encodeURIComponent(fullId)}`;
}

export function downloadOlifelInvoicePdf(fullId) {
    return axiosInstance.get(
        `${V2}/olifel/invoice/pdf?full_id=${encodeURIComponent(fullId)}`,
        { responseType: "blob" }
    );
}

export function sendOlifelInvoiceEmail(fullId, email) {
    return axiosInstance.get(
        `${V2}/olifel/invoice/send-email?full_id=${encodeURIComponent(fullId)}&email=${encodeURIComponent(email)}`
    );
}

// ── O.8: área de retenção + observabilidade ──────────────────────

export function getOlifelPendingActions(filters = {}) {
    // filters = { status?, action_type?, since?, page?, limit? }
    return axiosInstance.post(`${V2}/olifel/pending-actions`, filters);
}

export function resolveOlifelPendingAction(payload) {
    // payload = { id, action: 'restore'|'partial'|'skip', partial_qty? }
    return axiosInstance.post(`${V2}/olifel/pending-actions/resolve`, payload);
}

export function getOlifelOperationLog(filters = {}) {
    // filters = { direction?, errors_only?, since?, part_id?, o_document_id?, page?, limit? }
    return axiosInstance.post(`${V2}/olifel/operation-log`, filters);
}
